// Fase 8 (rev) — Dettaglio richiamo. L'esito è un movimento reale in pipeline
// + nota (sul tenant), che chiude il richiamo. "Prenotato" apre la prenotazione.
import { useEffect, useState } from "react";
import { X, PhoneCall, Store, IdCard, CalendarClock, Ban, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ApiProvider } from "@/lib/apiClient";
import QuickAppointmentModal from "@/components/QuickAppointmentModal";
import { callbackTone, cbApiErr, fmtDateTime, notifyCallbackGoogleSync } from "@/lib/callbacks";

function pad(n) { return String(n).padStart(2, "0"); }
function toLocalInput(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const isBookedStage = (label) => /prenot/i.test(label || "");

export default function CallbackDetail({ callback, tenantClient, onClose, onChanged, onOpenContact }) {
  const [mode, setMode] = useState(null); // null | reschedule | esito
  const [when, setWhen] = useState(() => toLocalInput(callback.scheduled_at));
  const [duration, setDuration] = useState(callback.duration_minutes || 20);
  const [busy, setBusy] = useState(false);
  const [stages, setStages] = useState([]);
  const [stageKey, setStageKey] = useState("");
  const [note, setNote] = useState("");
  const [showAppt, setShowAppt] = useState(false);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    if (mode !== "esito" || !tenantClient) return;
    tenantClient.get("/pipeline/stages").then((r) => setStages(r.data || [])).catch(() => setStages([]));
  }, [mode, tenantClient]);

  const tone = callbackTone(callback);
  const editable = callback.status === "scheduled";
  const selectedStage = stages.find((s) => s.key === stageKey);

  const reschedule = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/agency/callbacks/${callback.id}`, {
        scheduled_at: new Date(when).toISOString(), duration_minutes: Number(duration),
      });
      notifyCallbackGoogleSync(r.data?.google_sync_status, { rescheduled: true });
      onChanged?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || cbApiErr(e, "Orario non disponibile"));
    } finally { setBusy(false); }
  };

  const confirmEsito = async () => {
    if (!stageKey) { toast.error("Seleziona la fase pipeline"); return; }
    if (!note.trim()) { toast.error("Aggiungi una nota sull'esito"); return; }
    setBusy(true);
    try {
      await tenantClient.patch(`/pipeline/${callback.client_id}/stage`, { stage: stageKey });
      await tenantClient.post(`/clients/${callback.client_id}/activities/manual`, { text: note.trim() });
      await api.post(`/agency/callbacks/${callback.id}/complete`, {
        outcome: isBookedStage(selectedStage?.label) ? "booked" : "other" });
      toast.success("Esito registrato");
      onChanged?.(); onClose();
    } catch (e) { toast.error(cbApiErr(e, "Errore nel salvataggio esito")); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Annullare questo richiamo?")) return;
    setBusy(true);
    try {
      await api.post(`/agency/callbacks/${callback.id}/cancel`, {});
      toast.success("Richiamo annullato"); onChanged?.(); onClose();
    } catch (e) { toast.error(cbApiErr(e, "Errore")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex justify-end" onClick={onClose} data-testid="callback-detail">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[color:var(--sp-border)] px-6 py-4 flex items-start justify-between">
          <div>
            <h3 className="font-display text-2xl font-black flex items-center gap-2">
              <PhoneCall className="h-5 w-5 text-[color:var(--sp-brand)]" /> Richiamo
            </h3>
            <span className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full border text-[10px] font-bold ${tone.cls}`} data-testid="callback-detail-status">{tone.label}</span>
          </div>
          <button onClick={onClose} data-testid="callback-detail-close" className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[color:var(--sp-subtle)]"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <div className="font-black text-lg">{callback.client?.name || "Contatto"}</div>
            <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-brand)] flex items-center gap-1 mt-0.5">
              <Store className="h-3 w-3" /> {callback.tenant?.name || "—"}
            </div>
            {callback.client?.phone && <div className="text-sm text-[color:var(--sp-text-muted)] mt-1">{callback.client.phone}</div>}
          </div>

          <div className="rounded-xl border border-[color:var(--sp-border)] p-3 text-sm flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-[color:var(--sp-brand)]" /> <b>{fmtDateTime(callback.scheduled_at)}</b> · {callback.duration_minutes} min
          </div>

          <button data-testid="callback-open-contact" onClick={() => onOpenContact?.(callback)}
            className="sp-btn-ghost inline-flex items-center gap-2 text-sm w-full justify-center">
            <IdCard className="h-4 w-4" /> Apri scheda contatto
          </button>

          {editable && (
            <>
              {/* Esito = movimento pipeline + nota */}
              {mode !== "esito" ? (
                <button data-testid="callback-esito-open" onClick={() => setMode("esito")}
                  className="sp-btn-primary inline-flex items-center gap-2 w-full justify-center"><ClipboardCheck className="h-4 w-4" /> Registra esito</button>
              ) : (
                <div className="rounded-xl border border-[color:var(--sp-border)] p-3 space-y-2">
                  <div className="text-sm font-black">Esito della chiamata</div>
                  <label className="text-xs font-bold">Sposta in pipeline</label>
                  <select data-testid="callback-esito-stage" className="sp-input" value={stageKey} onChange={(e) => setStageKey(e.target.value)}>
                    <option value="">Seleziona fase…</option>
                    {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                  {selectedStage && isBookedStage(selectedStage.label) && (
                    <button data-testid="callback-esito-book" onClick={() => setShowAppt(true)}
                      className="sp-btn-ghost inline-flex items-center gap-2 text-sm w-full justify-center"><CalendarClock className="h-4 w-4" /> Prenota appuntamento</button>
                  )}
                  <label className="text-xs font-bold">Nota esito</label>
                  <textarea data-testid="callback-esito-note" className="sp-input" rows={3} value={note}
                    onChange={(e) => setNote(e.target.value)} placeholder="Es. Non risponde / Prenotato taglio martedì…" />
                  <div className="flex gap-2">
                    <button onClick={() => setMode(null)} className="sp-btn-ghost text-sm">Annulla</button>
                    <button data-testid="callback-esito-confirm" disabled={busy} onClick={confirmEsito} className="sp-btn-primary flex-1 text-sm">Conferma esito</button>
                  </div>
                </div>
              )}

              {/* Riprogramma */}
              {mode !== "reschedule" ? (
                <button data-testid="callback-reschedule-open" onClick={() => setMode("reschedule")}
                  className="sp-btn-ghost inline-flex items-center gap-2 w-full justify-center text-sm"><CalendarClock className="h-4 w-4" /> Riprogramma richiamo</button>
              ) : (
                <div className="rounded-xl border border-[color:var(--sp-border)] p-3">
                  <input type="datetime-local" data-testid="callback-reschedule-input" className="sp-input" value={when} onChange={(e) => setWhen(e.target.value)} />
                  <input type="number" min="5" step="5" className="sp-input mt-2" value={duration} onChange={(e) => setDuration(e.target.value)} />
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => setMode(null)} className="sp-btn-ghost text-sm">Annulla</button>
                    <button data-testid="callback-reschedule-save" disabled={busy} onClick={reschedule} className="sp-btn-primary flex-1 text-sm">Salva</button>
                  </div>
                </div>
              )}

              <button data-testid="callback-cancel-btn" disabled={busy} onClick={cancel}
                className="inline-flex items-center gap-2 w-full justify-center text-sm text-[color:var(--sp-danger)] py-2"><Ban className="h-4 w-4" /> Annulla richiamo</button>
            </>
          )}
        </div>
      </div>

      {showAppt && tenantClient && (
        <ApiProvider client={tenantClient}>
          <QuickAppointmentModal clientId={callback.client_id} onClose={() => setShowAppt(false)}
            onSaved={() => { setShowAppt(false); toast.success("Appuntamento creato"); }} />
        </ApiProvider>
      )}
    </div>
  );
}
